import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";

const LINKS = [
  { name: "Home", path: "/" },
  { name: "Tools", path: "/tools" },
  { name: "Blog", path: "/blog" },
  { name: "AI Chat", path: "/chat" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-border shadow-soft" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Sparkles className="h-4 w-4 text-gold" strokeWidth={1.6} />
          </span>
          <span className="font-serif text-2xl tracking-tight text-foreground">Numoraa</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-${l.name.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors ${
                  isActive ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:block">
          <Link
            to="/tools/numerology-calculator"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold"
          >
            Free Reading
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </Link>
        </div>

        <button
          className="md:hidden p-2 text-foreground"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-border overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col">
              {LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  className="py-3 text-base border-b border-border/60 text-foreground"
                >
                  {l.name}
                </NavLink>
              ))}
              <Link to="/tools/numerology-calculator" className="mt-4 rounded-full bg-primary px-5 py-3 text-center text-primary-foreground">
                Free Reading →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
